import type { ProductImageProps } from '@components/ProductImage';
import type { ProductListItem } from '@providers/ProductListProvider';
import { useProductItemThemeSettings } from '@providers/ThemeProvider/Provider/ProductItemThemeSettings';
import { useMemo } from 'react';

import type { ProductCTAPropsMapSettings } from './connectCTAButton';
import { useConnectCTAButton } from './connectCTAButton';
import type { ProductLabelPropsMapSettings } from './connectProductLabel';
import { useConnectProductLabel } from './connectProductLabel';
import type { ProductRatingPropsMapSettings } from './connectProductRating';
import { useConnectProductRating } from './connectProductRating';
import type { ProductSwatchesPropsMapSettings } from './connectProductSwatches';
import { useConnectProductSwatches } from './connectProductSwatches';

export type ProductImgPropsMapSettings = Pick<
  ProductImageProps,
  | 'aspectRatio'
  | 'isAutoRatio'
  | 'manualAspectRatio'
  | 'hoverEffect'
  | 'grid'
  | 'cropPosition'
  | 'loadedImageSizeStrategy'
> & {
  elements: {
    saleLabel?: ProductLabelPropsMapSettings;
    soldOutLabel?: ProductLabelPropsMapSettings;
    customLabelByTag?: ProductLabelPropsMapSettings;
    swatches?: ProductSwatchesPropsMapSettings;
    cta?: ProductCTAPropsMapSettings;
    rating?: ProductRatingPropsMapSettings;
  };
};

export const useConnectProductImg = (product: ProductListItem) => {
  const settings = useProductItemThemeSettings();
  const { props: productSwatchesProps } = useConnectProductSwatches('img', product);
  const { props: productCTAProps } = useConnectCTAButton('img', product);
  const { props: productRatingProps } = useConnectProductRating('img');
  const { props: productSaleLabelProps } = useConnectProductLabel('productSaleLabel');
  const { props: productSoldOutLabelProps } = useConnectProductLabel('productSoldOutLabel');
  const { props: productCustomLabelByTagProps } = useConnectProductLabel('productCustomLabelByTag');
  const productImgSettings = settings.productImg;

  const props: ProductImgPropsMapSettings = useMemo(() => {
    return {
      grid: productImgSettings.grid,
      aspectRatio: productImgSettings.aspectRatioType,
      isAutoRatio: productImgSettings.aspectRatioType === 'auto',
      manualAspectRatio: productImgSettings.manualAspectRatio,
      cropPosition: productImgSettings.cropPosition,
      hoverEffect: productImgSettings.hoverEffect,
      elements: {
        swatches: productSwatchesProps,
        cta: productCTAProps,
        rating: productRatingProps,
        saleLabel: productSaleLabelProps,
        soldOutLabel: productSoldOutLabelProps,
        customLabelByTag: productCustomLabelByTagProps,
      },
      loadedImageSizeStrategy: productImgSettings.loadedImageSizeStrategy,
    };
  }, [
    productImgSettings,
    productSwatchesProps,
    productCTAProps,
    productRatingProps,
    productSaleLabelProps,
    productSoldOutLabelProps,
    productCustomLabelByTagProps,
  ]);

  return {
    props,
  };
};
